import React, { useCallback, useEffect, useState } from "react";
import { PhoneCall, Save, Loader2, KeyRound } from "lucide-react";
import api from "../lib/api";
import LiveCalls from "./LiveCalls";

const FIELDS = [
  ["ws_url", "SIP WebSocket URL", "wss://sip.mmdsmart.com:7443"],
  ["domain", "SIP domain / realm", "sip.mmdsmart.com"],
  ["outbound_prefix", "Outbound prefix", "(usually blank)"],
  ["default_country", "Default country code", "44"],
  ["stun", "STUN server", "stun:stun.l.google.com:19302"],
  ["turn_url", "TURN server", "turn:turn.example.com:3478"],
  ["turn_user", "TURN username", ""],
  ["turn_pass", "TURN password", ""],
  ["listen_prefix", "Listen feature code", "*555"],
  ["whisper_prefix", "Whisper feature code", "*556"],
  ["barge_prefix", "Join / barge feature code", "*557"],
];

export default function TelephonyTab() {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/telephony/settings"), api.get("/users")]);
      setCfg(a.data);
      setUsers(b.data.users || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load telephony settings.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try { const { data } = await api.put("/telephony/settings", cfg); setCfg(data); setMsg("Saved."); }
    catch (e) { setErr(e.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const saveCreds = async (u, creds) => {
    try {
      await api.put(`/telephony/users/${u.id}/sip`, creds);
      setMsg(`SIP credentials updated for ${u.full_name}.`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not save credentials."); }
  };

  if (!cfg) return <Loader2 className="animate-spin text-slate-400" />;

  return (
    <div className="space-y-6" data-testid="telephony-tab">
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
        <div className="flex items-center gap-2 mb-1">
          <PhoneCall size={16} className="text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-slate-900 dark:text-white text-sm flex-1">MMDSmart connection</h3>
          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <input type="checkbox" checked={!!cfg.enabled} data-testid="tel-enabled"
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
            Enabled
          </label>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
          These values come from your MMDSmart account. Calls are placed over SIP / WebRTC straight from the
          agent&apos;s browser. Ask MMDSmart to confirm the three supervision feature codes for your trunk.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, label, ph]) => (
            <label key={k} className="block">
              <span className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">{label}</span>
              <input value={cfg[k] || ""} placeholder={ph} data-testid={`tel-${k}`}
                type={k.indexOf("pass") >= 0 ? "password" : "text"}
                onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500" />
            </label>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={save} disabled={busy} data-testid="tel-save"
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white text-sm font-semibold flex items-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
          {msg && <span className="text-xs text-emerald-600 dark:text-emerald-400">{msg}</span>}
          {err && <span className="text-xs text-rose-600 dark:text-rose-400">{err}</span>}
        </div>
      </div>

      <LiveCalls />

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <KeyRound size={16} className="text-slate-500 dark:text-slate-400" />
          <h3 className="font-semibold text-slate-900 dark:text-white text-sm flex-1">Agent SIP credentials</h3>
          <span className="text-xs text-slate-400">issued by MMDSmart, one per agent</span>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {users.map((u) => <CredRow key={u.id} u={u} onSave={saveCreds} />)}
          {!users.length && <p className="p-4 text-sm text-slate-500 dark:text-slate-400">No users.</p>}
        </div>
      </div>
    </div>
  );
}

function CredRow({ u, onSave }) {
  const [v, setV] = useState({
    sip_username: u.sip_username || "",
    sip_password: "",
    sip_extension: u.sip_extension || "",
  });
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    const body = { sip_username: v.sip_username, sip_extension: v.sip_extension };
    if (v.sip_password) body.sip_password = v.sip_password;   // blank means "leave it alone"
    await onSave(u, body);
    setV({ ...v, sip_password: "" });
    setSaving(false);
  };
  return (
    <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
      <div className="w-40 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{u.full_name}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{u.role}</p>
      </div>
      <input value={v.sip_username} onChange={(e) => setV({ ...v, sip_username: e.target.value })}
        placeholder="SIP username" data-testid={`sip-user-${u.id}`}
        className="flex-1 min-w-[120px] px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs text-slate-900 dark:text-slate-100 outline-none" />
      <input value={v.sip_password} onChange={(e) => setV({ ...v, sip_password: e.target.value })}
        placeholder="password (blank = keep)" type="password" data-testid={`sip-pass-${u.id}`}
        className="flex-1 min-w-[120px] px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs text-slate-900 dark:text-slate-100 outline-none" />
      <input value={v.sip_extension} onChange={(e) => setV({ ...v, sip_extension: e.target.value })}
        placeholder="ext" data-testid={`sip-ext-${u.id}`}
        className="w-20 px-2 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs text-slate-900 dark:text-slate-100 outline-none" />
      <button onClick={go} disabled={saving} data-testid={`sip-save-${u.id}`}
        className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">
        {saving ? "…" : "Save"}
      </button>
    </div>
  );
}
